import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  LinearProgress,
  Alert,
} from '@mui/material';
import { ArrowBack, ArrowForward, Save, Check } from '@mui/icons-material';
import { apiService } from '../services/api';
import { getCategoryNames, getSurveyQuestionsByCategory } from '../utils/surveyQuestions';
import SiteFooter from '../components/SiteFooter';

const Page: React.FC<{ children: React.ReactNode; center?: boolean }> = ({ children, center }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ flex: 1, display: center ? 'flex' : 'block', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </Box>
    <SiteFooter />
  </Box>
);

const SurveyWizard: React.FC = () => {
  const { groupId, participantId } = useParams<{ groupId: string; participantId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const categories = getCategoryNames();
  const questionsByCategory = getSurveyQuestionsByCategory();

  useEffect(() => {
    loadExistingResponses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantId]);

  const loadExistingResponses = async () => {
    if (!participantId) return;

    try {
      const existingResponse = await apiService.getSurveyResponse(participantId);
      if (existingResponse) {
        setResponses(existingResponse.responses);
      }
    } catch (error: any) {
      // No existing response is fine
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (questionId: string, value: any) => {
    setResponses({ ...responses, [questionId]: value });
  };

  const handleCheckboxChange = (questionId: string, option: string, checked: boolean) => {
    const currentValues = responses[questionId] || [];
    if (checked) {
      setResponses({ ...responses, [questionId]: [...currentValues, option] });
    } else {
      setResponses({
        ...responses,
        [questionId]: currentValues.filter((v: string) => v !== option),
      });
    }
  };

  const handleSave = async () => {
    if (!participantId || !groupId) return;

    setSaving(true);
    setError(null);

    try {
      await apiService.submitSurveyResponse(participantId, groupId, responses);
      setSuccess(true);
      setTimeout(() => {
        navigate(`/g/${groupId}`);
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to save survey responses');
    } finally {
      setSaving(false);
    }
  };

  const renderQuestion = (question: any) => {
    const value = responses[question.id] || '';

    switch (question.type) {
      case 'text':
        return (
          <TextField
            fullWidth
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder="Type your answer..."
            variant="outlined"
          />
        );

      case 'textarea':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder="Type your answer..."
            variant="outlined"
          />
        );

      case 'radio':
        return (
          <RadioGroup
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
          >
            {question.options?.map((option: string) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
        );

      case 'checkbox':
        return (
          <FormGroup>
            {question.options?.map((option: string) => (
              <FormControlLabel
                key={option}
                control={
                  <Checkbox
                    checked={(value || []).includes(option)}
                    onChange={(e) =>
                      handleCheckboxChange(question.id, option, e.target.checked)
                    }
                  />
                }
                label={option}
              />
            ))}
          </FormGroup>
        );

      case 'select':
        return (
          <Select
            fullWidth
            value={value}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            displayEmpty
          >
            <MenuItem value="">
              <em>Select one...</em>
            </MenuItem>
            {question.options?.map((option: string) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );

      default:
        return null;
    }
  };

  const currentCategory = categories[currentStep];
  const currentQuestions = questionsByCategory[currentCategory] || [];
  const progress = ((currentStep + 1) / categories.length) * 100;

  const isLastStep = currentStep === categories.length - 1;
  const isFirstStep = currentStep === 0;

  if (loading) {
    return (
      <Page center>
        <Typography variant="h6" sx={{ fontFamily: 'inherit' }}>
          Loading survey...
        </Typography>
      </Page>
    );
  }

  if (success) {
    return (
      <Page center>
        <Card sx={{ maxWidth: 500, p: 4, textAlign: 'center' }}>
          <Check sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Survey Saved!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Thank you for completing the survey. Redirecting...
          </Typography>
        </Card>
      </Page>
    );
  }

  return (
      <Page>
      <Container maxWidth="md" sx={{ pt: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h2" sx={{ color: 'primary.main', fontSize: { xs: '2.2rem', md: '2.8rem' }, mb: 1 }}>
            Gift preferences
          </Typography>
          <Typography color="text.secondary">
            Sizes, favorites, and things to skip. Only your Secret Santa and the organizer can see this.
          </Typography>
        </Box>

        {/* Progress */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress
              </Typography>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress variant="determinate" value={progress} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {currentStep + 1} / {categories.length}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Survey Form */}
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
              {currentCategory}
            </Typography>

            {currentQuestions.map((question, index) => (
              <Box key={question.id} sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  {question.text}
                  {question.required && (
                    <Typography component="span" color="error">
                      {' '}
                      *
                    </Typography>
                  )}
                </Typography>
                {renderQuestion(question)}
              </Box>
            ))}
          </CardContent>
        </Card>

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, mb: 4 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => setCurrentStep(currentStep - 1)}
            disabled={isFirstStep}
            sx={{ bgcolor: 'background.paper' }}
          >
            Previous
          </Button>

          {isLastStep ? (
            <Button
              variant="contained"
              endIcon={<Save />}
              onClick={handleSave}
              disabled={saving}
              sx={{ px: 3 }}
            >
              {saving ? 'Saving...' : 'Save Survey'}
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={() => setCurrentStep(currentStep + 1)}
            >
              Next
            </Button>
          )}
        </Box>
      </Container>
    </Page>
  );
};

export default SurveyWizard;

